import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, FABRICS } from "@/data/content";

const initial = {
  company: "",
  contact: "",
  address: "",
  email: "",
  whatsapp: "",
  fabric: "",
  quantity: "",
  message: "",
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-slate-400 focus:border-gold";

const SamplesModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    onClose();
    setSent(null);
    setForm(initial);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/samples`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setSent({ ...data, contact: form.contact });
      toast.success("Enquiry received — our export desk has it.");
    } catch {
      toast.error("Could not send just now. Please email contact@painternational.in.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="request-samples-modal"
          className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto bg-white"
          >
            <div className="flex items-start justify-between border-b border-gold/30 px-7 py-6 sm:px-10">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-royal">Export Desk</p>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-navy sm:text-3xl">Request Samples</h3>
                <p className="mt-2 max-w-md text-xs leading-relaxed text-slate-500">
                  Samples are shared with verified businesses. Please include your full company details —
                  we respond within one business day, by email or WhatsApp.
                </p>
              </div>
              <button
                data-testid="sample-modal-close-btn"
                onClick={close}
                className="text-slate-400 transition-colors hover:text-navy"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            {sent ? (
              <div data-testid="sample-modal-success" className="px-7 py-10 sm:px-10">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-gold">
                  <Check size={22} />
                </div>
                <h4 className="mt-6 font-serif text-2xl text-navy">
                  Thank you, {sent.contact.split(" ")[0]}.
                </h4>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">
                  Your enquiry has reached our export desk and we will respond within one business
                  day. For the fastest reply, continue the conversation on WhatsApp — your details
                  are already attached to the message.
                </p>
                <a
                  data-testid="sample-modal-whatsapp-btn"
                  href={sent.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-7 inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:bg-navy hover:text-gold"
                >
                  <MessageCircle size={16} /> Continue on WhatsApp
                </a>
                <p className="mt-6 text-xs text-slate-400">
                  Prefer email? Write to{" "}
                  <a href={`mailto:${CONTACT.email}`} className="text-royal underline">
                    {CONTACT.email}
                  </a>
                </p>
                <button
                  data-testid="sample-modal-done-btn"
                  onClick={close}
                  className="mt-8 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-navy"
                >
                  Close
                </button>
              </div>
            ) : (
            <form onSubmit={submit} className="grid gap-5 px-7 py-8 sm:grid-cols-2 sm:px-10">
              <Field label="Company name" required>
                <input data-testid="sample-modal-company-name-input" required value={form.company} onChange={set("company")} className={inputCls} placeholder="Maison Example Ltd." />
              </Field>
              <Field label="Contact person" required>
                <input data-testid="sample-modal-contact-person-input" required value={form.contact} onChange={set("contact")} className={inputCls} placeholder="Full name" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Business address" required>
                  <input data-testid="sample-modal-address-input" required value={form.address} onChange={set("address")} className={inputCls} placeholder="Street, city, country" />
                </Field>
              </div>
              <Field label="Business email" required>
                <input data-testid="sample-modal-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="buying@company.com" />
              </Field>
              <Field label="WhatsApp number (with country code)" required>
                <input data-testid="sample-modal-whatsapp-input" required value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} placeholder="+44 7700 900000" />
              </Field>
              <Field label="Fabric of interest">
                <select data-testid="sample-modal-fabric-select" value={form.fabric} onChange={set("fabric")} className={inputCls}>
                  <option value="">Select a fabric family</option>
                  {FABRICS.map((f) => (
                    <option key={f.id} value={f.name}>{f.name}</option>
                  ))}
                  <option value="Multiple / Custom">Multiple / Custom</option>
                </select>
              </Field>
              <Field label="Estimated quantity">
                <input data-testid="sample-modal-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500 m / month" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Message">
                  <textarea data-testid="sample-modal-message-input" rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Composition, GSM, width, finish, end use…" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="sample-modal-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-navy py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Sample Request"}
                </button>
              </div>
            </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SamplesModal;
